import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ImageUploader = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading to /upload/image...');
      const response = await api.post('/upload/image', formData);
      console.log('Upload response:', response.data);

      // L'URL retournée est déjà au format /api/uploads/xxx.jpg
      const imageUrl = response.data.image_url;
      // Construire l'URL complète avec le domaine backend
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      
      console.log('Image URL from backend:', imageUrl);
      console.log('Full URL:', fullUrl);
      
      setPreviewUrl(fullUrl);
      onChange(fullUrl);
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || error.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      
      {previewUrl ? (
        <div className="relative w-full h-48 border-2 border-slate-200 rounded-lg overflow-hidden group">
          <img 
            src={previewUrl} 
            alt="Preview"
            className="w-full h-full object-cover"
            onError={() => {
              setPreviewUrl('');
              toast.error('Impossible de charger l\'image');
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              className="bg-red-600 hover:bg-red-700"
            >
              <X className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      ) : (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-rose-500 hover:bg-rose-50/50 transition-all"
        >
          {uploading ? (
            <>
              <Loader2 className="w-12 h-12 text-rose-600 animate-spin mb-3" />
              <p className="text-sm text-slate-600">Upload en cours...</p>
            </>
          ) : (
            <>
              <Upload className="w-12 h-12 text-slate-400 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-600 mb-1">Cliquez pour uploader une image</p>
              <p className="text-xs text-slate-400">PNG, JPG, JPEG (max 5 Mo)</p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <p className="text-xs text-slate-500">
        💡 Astuce : Utilisez des images de haute qualité pour un meilleur rendu
      </p>
    </div>
  );
};

export default ImageUploader;
